// Everything the inbox needs to tell one agent from another, keyed by agentType (the role key the
// server stamps on every task) — never by matching a skill name or a persona's first name.

export type AgentTone = 'accent' | 'info' | 'purple' | 'neutral';

/** The colour each agent wears in the inbox. Sam (seo) is the accent, Jenny (ads) is info-blue; an
 *  agent this map does not know renders neutral rather than borrowing another agent's colour. */
const TONE_BY_AGENT: Record<string, AgentTone> = {
  seo: 'accent',
  ads: 'info',
  community: 'purple',
};

export function agentTone(agentType: string | null | undefined): AgentTone {
  return (agentType && TONE_BY_AGENT[agentType]) || 'neutral';
}

export interface AgentIdentity {
  roleKey: string;
  personaName: string;
  displayName?: string;
}

/** The display name for an agent type, from the location's assigned agents (server-supplied persona
 *  names), else a readable fallback built from the role key — never a name invented client-side. */
export function agentDisplayName(
  agentType: string | null | undefined,
  agents: readonly AgentIdentity[] | undefined,
): string {
  if (!agentType) return 'Unknown agent';
  const known = agents?.find((a) => a.roleKey === agentType);
  if (known?.personaName) return known.personaName;
  return `${agentType.charAt(0).toUpperCase()}${agentType.slice(1)} agent`;
}

/**
 * Who the inbox can name: the location's assigned agents first, then — for an enabled agent that
 * is not assigned here — its persona from the role directory, so the same agent carries the same
 * name on every location. Only enabled agents are returned.
 */
export function resolveInboxAgents(
  assigned: readonly AgentIdentity[] | undefined,
  directory: readonly AgentIdentity[] | undefined,
  enabledAgents: ReadonlySet<string>,
): AgentIdentity[] {
  const out = (assigned ?? []).filter((a) => enabledAgents.has(a.roleKey));
  const seen = new Set(out.map((a) => a.roleKey));
  for (const entry of directory ?? []) {
    if (!enabledAgents.has(entry.roleKey) || seen.has(entry.roleKey)) continue;
    out.push(entry);
    seen.add(entry.roleKey);
  }
  return out;
}

/** One letter for the chip. */
export function agentInitial(name: string): string {
  const trimmed = name.trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
}

/** Plain-language label for a checkpoint's always-ask floor (Task.payload.floorReason). */
export const FLOOR_LABELS: Record<string, string> = {
  SPEND_COMMIT: 'Commits ad spend',
  GO_LIVE: 'Takes something live',
  COMPLIANCE_UNFIXABLE: 'Compliance issue needs a decision',
  PHI_ADJACENT: 'Touches patient-adjacent tracking',
  PATIENT_LIKENESS: 'Uses a patient photo or testimonial',
};

export function floorLabel(floorReason: string | null | undefined): string | null {
  if (!floorReason) return null;
  return FLOOR_LABELS[floorReason] ?? floorReason;
}

// ─── Agent filter persistence ─────────────────────────────────────────────────
// The chosen agent filter is a per-viewer convenience, so it lives in localStorage. Every read and
// write is guarded: storage can be absent, full, or blocked, and the inbox must render either way.

export const AGENT_FILTER_STORAGE_KEY = 'agents.tasks.agentFilter';
export const ALL_AGENTS = 'all';

// Structural, not the DOM `Storage` type: this module is compiled without DOM libs for the node
// test run, and only these two methods are ever used.
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function readStoredAgentFilter(
  storage: Pick<KeyValueStorage, 'getItem'> | undefined,
): string {
  try {
    const raw = storage?.getItem(AGENT_FILTER_STORAGE_KEY);
    return raw && raw.trim() ? raw : ALL_AGENTS;
  } catch {
    return ALL_AGENTS;
  }
}

export function writeStoredAgentFilter(
  storage: Pick<KeyValueStorage, 'setItem'> | undefined,
  value: string,
): void {
  try {
    storage?.setItem(AGENT_FILTER_STORAGE_KEY, value);
  } catch {
    // Best effort only.
  }
}

/** A stored filter is only honoured while that agent is still enabled for the practice; otherwise
 *  the inbox falls back to all agents rather than showing an empty list for a stale choice. */
export function effectiveAgentFilter(stored: string, enabledAgents: ReadonlySet<string>): string {
  return stored !== ALL_AGENTS && enabledAgents.has(stored) ? stored : ALL_AGENTS;
}
